import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./WaybillMonitor.css";

const SHOPS = [
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function getRows(data) {
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
}

export default function WaybillMonitor() {
  const [shop, setShop] = useState("ALL");
  const [tab, setTab] = useState("pending");
  const [pendingRows, setPendingRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function fetchJson(url) {
    const res = await fetch(url);
    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "API request failed");
    }

    return data;
  }

  async function loadWaybills() {
    try {
      setLoading(true);
      setError("");

      const targetShops = shop === "ALL" ? SHOPS : [shop];

      const pendingResult = await Promise.all(
        targetShops.map(async (s) => {
          const data = await fetchJson(`${API_BASE}/api/waybill-pending/${s}`);
          return getRows(data).map((row) => ({
            ...row,
            currentShop: row.currentShop || s,
          }));
        })
      );

      const historyResult = await Promise.all(
        targetShops.map(async (s) => {
          const data = await fetchJson(`${API_BASE}/api/waybill-history/${s}`);
          return getRows(data).map((row) => ({
            ...row,
            savedByShop: row.savedByShop || s,
          }));
        })
      );

      setPendingRows(pendingResult.flat());
      setHistoryRows(historyResult.flat());
    } catch (err) {
      setError(err.message || "Failed to load waybill monitor");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadWaybills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  const sourceShopCount = useMemo(() => {
    const set = new Set(pendingRows.map((r) => r.sourceShop).filter(Boolean));
    return set.size;
  }, [pendingRows]);

  const activeRows = tab === "pending" ? pendingRows : historyRows;

  return (
    <Layout title="Waybill Monitor">
      <div className="wb">
        <div className="wb__hero">
          <div>
            <div className="wb__eyebrow">Fulfillment Control</div>
            <h2>Royal Waybill Monitor</h2>
            <p>
              Monitor pending waybills, saved history and shop fulfillment status.
            </p>
          </div>

          <div className="wb__actions">
            <select value={shop} onChange={(e) => setShop(e.target.value)}>
              <option value="ALL">All Shops</option>
              {SHOPS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <button onClick={loadWaybills} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && <div className="wb__error">{error}</div>}

        <div className="wb__cards">
          <div className="wbCard pending">
            <span>Pending Waybills</span>
            <strong>{formatNumber(pendingRows.length)}</strong>
          </div>

          <div className="wbCard done">
            <span>Saved History</span>
            <strong>{formatNumber(historyRows.length)}</strong>
          </div>

          <div className="wbCard">
            <span>Selected Shop</span>
            <strong>{shop}</strong>
          </div>

          <div className="wbCard">
            <span>Source Shops</span>
            <strong>{formatNumber(sourceShopCount)}</strong>
          </div>
        </div>

        <div className="wb__tabs">
          <button
            className={"wb__tab" + (tab === "pending" ? " is-active" : "")}
            onClick={() => setTab("pending")}
          >
            Pending
          </button>

          <button
            className={"wb__tab" + (tab === "history" ? " is-active" : "")}
            onClick={() => setTab("history")}
          >
            History
          </button>
        </div>

        <section className="wbPanel">
          <div className="wbPanel__head">
            <div>
              <h3>{tab === "pending" ? "Pending Waybills" : "Saved History"}</h3>
              <p>
                {tab === "pending"
                  ? "Rows still waiting for Royal Waybill No."
                  : "Rows already saved with Royal Waybill No."}
              </p>
            </div>
          </div>

          <div className="wbTableWrap">
            <table className="wbTable">
              <thead>
                <tr>
                  <th>Voucher</th>
                  <th>Source</th>
                  <th>{tab === "pending" ? "Current Shop" : "Saved By"}</th>
                  <th>Status</th>
                  <th>Waybill No</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Date</th>
                </tr>
              </thead>

              <tbody>
                {activeRows.map((row, index) => (
                  <tr key={row.id || `${row.voucherNo}-${index}`}>
                    <td>
                      <span className="wbBadge">{row.voucherNo || "-"}</span>
                    </td>
                    <td>{row.sourceShop || "-"}</td>
                    <td>
                      {tab === "pending"
                        ? row.currentShop || "-"
                        : row.savedByShop || "-"}
                    </td>
                    <td>
                      <span
                        className={
                          tab === "pending"
                            ? "wbStatus pending"
                            : "wbStatus done"
                        }
                      >
                        {tab === "pending" ? "PENDING" : "DONE"}
                      </span>
                    </td>
                    <td>{row.waybillNo || row.royalWaybillNo || "-"}</td>
                    <td>{row.customerName || "-"}</td>
                    <td>{row.customerPhone || "-"}</td>
                    <td>{row.cityMm || row.city || "-"}</td>
                    <td>{formatDate(row.savedAt || row.dateTime || row.createdAt)}</td>
                  </tr>
                ))}

                {!loading && activeRows.length === 0 && (
                  <tr>
                    <td colSpan="9">
                      <div className="wbEmpty">No waybill data found.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Layout>
  );
}


